import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { LoginResponse, RefreshResponse, UserClaimsShape } from "../types/common";
import { AUTH_API_PREFIX } from "./authApiPaths";

export interface AuthUser {
  userId: string;
  username: string;
  roles: string[];
  claims: UserClaimsShape;
  /** Returns true when the user's JWT grants the given scope string. */
  hasScope(scope: string): boolean;
}

interface PersistedAuthSlice {
  // refreshToken intentionally omitted — stored only in httpOnly Secure cookie set by pks-bff.
  // Persisting it in localStorage would expose it to XSS attacks.
  user: AuthUser | null;
}

interface AuthState extends PersistedAuthSlice {
  accessToken: string | null;
  accessExpiresAt: number | null;
  setFromLogin: (res: LoginResponse) => void;
  setFromRefresh: (res: RefreshResponse) => void;
  updateClaims: (claims: UserClaimsShape) => void;
  refreshClaims: () => Promise<void>;
  clearSession: () => void;
}

function makeAuthUser(claims: UserClaimsShape): AuthUser {
  return {
    userId: claims.user_id,
    username: claims.username,
    roles: claims.roles ?? [],
    claims,
    hasScope(scope: string) {
      return this.claims.scopes?.includes(scope) ?? false;
    },
  };
}

/** Re-attach the `hasScope` method after JSON deserialization from localStorage. */
function hydrateUser(raw: AuthUser | null): AuthUser | null {
  if (!raw) return null;
  if (typeof raw.hasScope === "function") return raw;
  return makeAuthUser(raw.claims);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      accessExpiresAt: null,
      setFromLogin: (res) =>
        set({
          accessToken: res.access_token,
          // refresh_token is NOT stored in JS state — the server sets an httpOnly cookie.
          accessExpiresAt: res.access_expires_at,
          user: makeAuthUser(res.claims),
        }),
      setFromRefresh: (res) =>
        set({
          accessToken: res.access_token,
          accessExpiresAt: res.access_expires_at,
        }),
      updateClaims: (claims) =>
        set({
          user: makeAuthUser(claims),
        }),
      refreshClaims: async () => {
        const token = useAuthStore.getState().accessToken;
        if (!token) return;
        const res = await fetch(`${AUTH_API_PREFIX}/session`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) return;
        const claims = (await res.json()) as UserClaimsShape;
        set({ user: makeAuthUser(claims) });
      },
      clearSession: () => {
        try {
          localStorage.removeItem("atlas-auth-storage");
        } catch {
          /* ignore */
        }
        set({
          accessToken: null,
          user: null,
          accessExpiresAt: null,
        });
      },
    }),
    {
      name: "atlas-auth-storage",
      partialize: (s): PersistedAuthSlice => ({
        // Only persist minimal user display info (username/roles for sidebar).
        // The access token is memory-only; refresh is handled by httpOnly cookie.
        user: s.user,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          state.user = hydrateUser(state.user);
        }
      },
    },
  ),
);
